import { RyeModalContent, useRyeModal } from "../rye/RyeModal";
import { buildClasses } from "../utils/buildClasses";
import { useCallback, useEffect, useRef, useState, useContext } from "react";
import { usePlaidLink } from "react-plaid-link";
import { callFirebaseFunction } from "../firebaseio/firebaseFunctions";
import { lineWobble } from "ldrs";
import { UserContext } from "../firebaseio/UserContext";
import { AccountsContext } from "../firebaseio/AccountsContext";
import { AccountStatuses, BreadAccount } from "breadcommon";
import {
  firestoreDeleteAccountById,
  firestoreUpdateAccount,
  firestoreCloseAccount,
} from "../firebaseio/firestoreIo";
import { WriteBatch } from "firebase/firestore";
import {
  PlaidLinkLoaderModalContent,
  ConfirmAccountsModalContent,
  FinalizingSyncModalContent,
  UpdatePreLinkNoteModalContent,
} from "./AddNewAccountModal";
import { useInstitutionFromAccount } from "../derived/useInstitutionFromAccount";
import { RyeButton } from "../rye/RyeButton";
import React from "react";

lineWobble.register();

enum ReplaceDisappearedAccountFlowState {
  START,
  UPDATE_PRE_LINK_NOTE,
  UPDATE_PLAID_LINK_OPENING,
  UPDATE_INITIALIZING_ACCOUNTS,
  UPDATE_CONFIRMING_ACCOUNTS,
  UPDATE_SYNCING,
}

export function ReplaceDisappearedAccountModal(props: {
  account: BreadAccount;
}) {
  const { closeModal } = useRyeModal();
  const user = useContext(UserContext);
  const accounts = useContext(AccountsContext);
  const accountUpdatePromises = useRef<Promise<void | WriteBatch>[]>([]);

  const plaidItemId = props.account.plaid_item_id!;
  const institution = useInstitutionFromAccount(props.account);
  const institutionName = institution?.name || "your bank";

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [needsLinkToken, setNeedsLinkToken] = useState<boolean>(true);
  const [flowState, setFlowState] = useState(
    ReplaceDisappearedAccountFlowState.START,
  );
  const [expectedNewAccounts, setExpectedNewAccounts] = useState<number | null>(
    null,
  );

  console.log("accounts:", accounts);

  const getLinkToken = useCallback(async () => {
    setNeedsLinkToken(false);
    try {
      const result = await callFirebaseFunction("GetPlaidLinkUpdateToken", {
        plaid_item_id: plaidItemId,
        account_selection_enabled: true,
      });

      if (!result.data.success || !result.data.linkToken) {
        console.log("Failed to get link token: ", result.data.error_msg);
        setNeedsLinkToken(true);
        return;
      }
      setNeedsLinkToken(false);
      setLinkToken(result.data.linkToken.link_token);
    } catch (err) {
      console.error("Caught error getting link token:", err);
      setNeedsLinkToken(true);
    }
  }, [plaidItemId]);

  const plaidLink = usePlaidLink({
    onSuccess: async () => {
      setFlowState(
        ReplaceDisappearedAccountFlowState.UPDATE_INITIALIZING_ACCOUNTS,
      );
    },
    onExit: () => {
      setFlowState(ReplaceDisappearedAccountFlowState.START);
    },
    token: linkToken,
  });

  useEffect(() => {
    if (needsLinkToken) {
      getLinkToken();
    }
  }, [needsLinkToken, getLinkToken]);

  useEffect(() => {
    if (
      flowState ===
        ReplaceDisappearedAccountFlowState.UPDATE_PLAID_LINK_OPENING &&
      linkToken !== null &&
      plaidLink.ready
    ) {
      plaidLink.open();
    }
  }, [flowState, linkToken, plaidLink]);

  // Handle initialization of updated accounts on Plaid Link success
  useEffect(() => {
    if (
      flowState ===
      ReplaceDisappearedAccountFlowState.UPDATE_INITIALIZING_ACCOUNTS
    ) {
      const init = async () => {
        try {
          const result = await callFirebaseFunction(
            "InitializePlaidUpdateAccounts",
            {
              plaid_item_id: plaidItemId,
            },
          );
          if (result.data.success) {
            const numNewAccounts = result.data.numNewAccounts ?? 0;
            setExpectedNewAccounts(numNewAccounts);
            if (numNewAccounts > 0) {
              setFlowState(
                ReplaceDisappearedAccountFlowState.UPDATE_CONFIRMING_ACCOUNTS,
              );
            } else {
              setFlowState(ReplaceDisappearedAccountFlowState.UPDATE_SYNCING);
            }
          } else {
            console.error(
              "Failed to initialize update accounts: ",
              result.data.error_msg,
            );
            setFlowState(ReplaceDisappearedAccountFlowState.START);
          }
        } catch (err) {
          console.error("Error initializing accounts:", err);
          setFlowState(ReplaceDisappearedAccountFlowState.START);
        }
      };
      void init();
    }
  }, [flowState, plaidItemId]);

  // Handle transaction & account sync once selection is completed/skipped
  useEffect(() => {
    if (flowState === ReplaceDisappearedAccountFlowState.UPDATE_SYNCING) {
      const runSyncs = async () => {
        try {
          await callFirebaseFunction("SyncTransactions", {});
          await callFirebaseFunction("SyncAccounts", {});
        } catch (err) {
          console.error("Error syncing transactions/accounts:", err);
        } finally {
          closeModal();
        }
      };
      void runSyncs();
    }
  }, [flowState, closeModal]);

  async function handleConfirm() {
    // Ensure all changes on candidate accounts are synced to Firestore
    await Promise.allSettled(accountUpdatePromises.current);

    let nextPositionNum = Array.from(accounts.values()).filter(
      (acc) =>
        acc.status !== AccountStatuses.NEW_SELECTED &&
        acc.status !== AccountStatuses.NEW_UNSELECTED,
    ).length;

    for (const [accountId, account] of Array.from(accounts)) {
      if (account.plaid_item_id === plaidItemId) {
        if (account.status === AccountStatuses.NEW_SELECTED) {
          await firestoreUpdateAccount(user.uid, accountId, {
            system_data: {
              status: AccountStatuses.ACTIVE,
            },
            manual_data: {
              order_position: nextPositionNum,
            },
          });
          nextPositionNum++;
        } else if (account.status === AccountStatuses.NEW_UNSELECTED) {
          await firestoreDeleteAccountById(user.uid, account, null);
        }
      }
    }

    setFlowState(ReplaceDisappearedAccountFlowState.UPDATE_SYNCING);
  }

  async function handleCancelConfirm() {
    for (const [, account] of Array.from(accounts)) {
      if (account.plaid_item_id === plaidItemId) {
        if (
          account.status === AccountStatuses.NEW_SELECTED ||
          account.status === AccountStatuses.NEW_UNSELECTED
        ) {
          await firestoreDeleteAccountById(user.uid, account, null);
        }
      }
    }

    await callFirebaseFunction("RemoveUnusedItemsAndInstitutions", {});
    closeModal();
  }

  const getModalView = (): React.ReactNode => {
    switch (flowState) {
      case ReplaceDisappearedAccountFlowState.START:
        return (
          <RyeModalContent
            title="Fix account connection"
            showCloseX={true}
            onClose={closeModal}
            content={
              <div
                className={buildClasses(
                  "w-[40rem]",
                  "flex",
                  "flex-col",
                  "gap-6",
                  "p-2",
                )}
              >
                <div className="text-sm text-on-surface-800 leading-relaxed">
                  We noticed we no longer have an active connection to this
                  account at {institutionName}. This can happen for a variety of
                  reasons, including if you closed the account or if you got a
                  new account number (e.g. if you got a new credit card number
                  after reporting fraud).
                </div>

                {/* Option 1: Reconnect */}
                <div className={buildClasses("flex", "flex-col")}>
                  <div
                    className={buildClasses(
                      "flex",
                      "items-center",
                      "justify-between",
                      "p-4",
                      "border",
                      "border-on-surface-200",
                      "rounded-lg",
                      "gap-8",
                    )}
                  >
                    <div
                      className={buildClasses(
                        "text-sm",
                        "text-on-surface-500",
                        "flex-1",
                      )}
                    >
                      If this account still exists, reconnect to{" "}
                      {institutionName} to get this account syncing again. Your
                      bank may just ask you to sign in again, or it may have you
                      link the account as if it were new. If it comes back as a
                      new account, you can always close this one afterwards.
                    </div>
                    <RyeButton
                      text="Reconnect"
                      vibe="primary"
                      variant="filled"
                      onClick={() => {
                        setFlowState(
                          ReplaceDisappearedAccountFlowState.UPDATE_PRE_LINK_NOTE,
                        );
                      }}
                    />
                  </div>
                </div>

                {/* Option 2: Close account */}
                <div className={buildClasses("flex", "flex-col")}>
                  <div
                    className={buildClasses(
                      "flex",
                      "items-center",
                      "justify-between",
                      "p-4",
                      "border",
                      "border-on-surface-200",
                      "rounded-lg",
                      "gap-8",
                    )}
                  >
                    <div
                      className={buildClasses(
                        "text-sm",
                        "text-on-surface-500",
                        "flex-1",
                      )}
                    >
                      If this account is now obsolete, you can mark it as
                      closed. Historical transactions will be kept, but the
                      account will stop syncing new transactions and the account
                      balance won't be used to calculate your net total. You
                      will be able to re-open the account if you want.
                    </div>
                    <RyeButton
                      text="Close this account"
                      vibe="ordinary"
                      variant="outlined"
                      onClick={async () => {
                        if (!user) return;
                        await firestoreCloseAccount(
                          user.uid,
                          props.account.id,
                          Array.from(accounts.values()),
                        );
                        closeModal();
                      }}
                    />
                  </div>
                </div>
              </div>
            }
            actionButtons={[
              {
                text: "Cancel",
                variant: "outlined",
                vibe: "subdued",
                action: closeModal,
              },
            ]}
          />
        );

      case ReplaceDisappearedAccountFlowState.UPDATE_PRE_LINK_NOTE:
        return (
          <UpdatePreLinkNoteModalContent
            title={`Reconnect to ${institutionName}`}
            description={
              <>
                We're going to use Plaid to securely reconnect to{" "}
                {institutionName}.
              </>
            }
            institutionName={institutionName}
            plaidItemId={plaidItemId}
            onBack={() => {
              setFlowState(ReplaceDisappearedAccountFlowState.START);
            }}
            onProceed={() => {
              setFlowState(
                ReplaceDisappearedAccountFlowState.UPDATE_PLAID_LINK_OPENING,
              );
            }}
            onClose={closeModal}
          />
        );

      case ReplaceDisappearedAccountFlowState.UPDATE_PLAID_LINK_OPENING:
      case ReplaceDisappearedAccountFlowState.UPDATE_INITIALIZING_ACCOUNTS:
        return (
          <PlaidLinkLoaderModalContent
            title={
              flowState ===
              ReplaceDisappearedAccountFlowState.UPDATE_PLAID_LINK_OPENING
                ? "Opening Plaid"
                : "Getting new accounts from Plaid"
            }
          />
        );

      case ReplaceDisappearedAccountFlowState.UPDATE_CONFIRMING_ACCOUNTS: {
        const updateCandidateAccounts = Array.from(accounts.values()).filter(
          (acc) =>
            acc.plaid_item_id === plaidItemId &&
            (acc.status === AccountStatuses.NEW_SELECTED ||
              acc.status === AccountStatuses.NEW_UNSELECTED),
        );

        const isLoadingCandidates =
          expectedNewAccounts !== null &&
          updateCandidateAccounts.length < expectedNewAccounts;

        if (isLoadingCandidates) {
          return (
            <RyeModalContent
              title="Select Accounts to Sync"
              showCloseX={false}
              content={
                <div
                  className={buildClasses(
                    "flex",
                    "flex-col",
                    "items-center",
                    "justify-center",
                    "py-10",
                    "text-on-surface-400",
                    "text-sm",
                  )}
                >
                  <l-line-wobble size="120" speed="2" color="#7d57ff" />
                  <div className="h-6" />
                  <div>Loading candidate accounts...</div>
                </div>
              }
              actionButtons={[]}
            />
          );
        }

        let numSelectedAccounts = 0;
        accounts.forEach((account: BreadAccount) => {
          if (
            account.plaid_item_id === plaidItemId &&
            account.status === AccountStatuses.NEW_SELECTED
          ) {
            numSelectedAccounts++;
          }
        });

        return (
          <ConfirmAccountsModalContent
            accountsList={updateCandidateAccounts}
            accountUpdatePromises={accountUpdatePromises}
            onCancel={handleCancelConfirm}
            onConfirm={handleConfirm}
            confirmText={`Add ${numSelectedAccounts} Account${
              numSelectedAccounts === 1 ? "" : "s"
            }`}
            confirmDisabled={numSelectedAccounts === 0}
          />
        );
      }

      case ReplaceDisappearedAccountFlowState.UPDATE_SYNCING:
        return <FinalizingSyncModalContent />;
      default:
        return null;
    }
  };

  return <div className={buildClasses("max-w-full")}>{getModalView()}</div>;
}
